import React, { useEffect, useMemo, useState } from 'react';
import { InternalApiCustomListSchemaField, InternalApiCustomListSchemaFieldType } from '../../api-internal';
import { ErrorMessage, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Col, Form as ReactForm, Row } from 'react-bootstrap';
import {
  SalvBanner,
  SalvBannerType,
  SalvBannerAccent,
  Button,
  SalvCheckbox,
  SalvCheckboxGroup,
  SalvDropdown,
  SalvGroupedCheckbox,
  SalvModal,
  SalvRadio,
  SalvRadioGroup,
  SalvTextField,
} from '@salvapp/ui-components';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { customListRecordApi } from '../../apis';
import * as Yup from 'yup';
import { ErrorResponse } from '../../common/errors/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { AppState } from '../../store/store';
import DisabledField from '../DisabledField';
import SchemaDropdownOptions from './SchemaDropdownOptionsDEPRECATED';
import './SchemaFieldModal.scss';
import { v4 as uuidv4 } from 'uuid';
import { useUpdateCustomListEntitySchemaFields } from './customListSchemaActions';
import { SchemaChildFieldsTable } from './SchemaChildFieldsTable';

export const customListSchemaFieldTypeOptions: DropdownOption[] = [
  {
    key: InternalApiCustomListSchemaFieldType.STRING,
    label: 'STRING',
    value: InternalApiCustomListSchemaFieldType.STRING,
  },
  {
    key: InternalApiCustomListSchemaFieldType.DATETIME,
    label: 'DATETIME',
    value: InternalApiCustomListSchemaFieldType.DATETIME,
  },
  {
    label: 'NUMBER',
    key: InternalApiCustomListSchemaFieldType.NUMBER,
    value: InternalApiCustomListSchemaFieldType.NUMBER,
  },
  {
    key: InternalApiCustomListSchemaFieldType.OBJECT,
    label: 'OBJECT',
    value: InternalApiCustomListSchemaFieldType.OBJECT,
  },
  {
    key: InternalApiCustomListSchemaFieldType.ENUM,
    label: 'ENUM',
    value: InternalApiCustomListSchemaFieldType.ENUM,
  },
];

const validationSchema = Yup.object()
  .shape({
    id: Yup.string(),
    displayName: Yup.string().required(),
    fieldName: Yup.string()
      .matches(/^\w+$/i, {
        message: 'Field name must contain letters, numbers or underscores',
      })
      .required(),
    isMulti: Yup.boolean().required(),
    isNullable: Yup.boolean().required(),
    visibleInMatches: Yup.boolean().required(),
    visibleInList: Yup.boolean().required(),
    type: Yup.string().required(),
    sortOrder: Yup.number().required(),
    childFields: Yup.array().of(
      Yup.object().shape({
        displayName: Yup.string().required(),
        fieldName: Yup.string().required(),
        type: Yup.string().required(),
        enumOptions: Yup.array().when('type', {
          is: 'ENUM',
          then: (schema) => schema.of(Yup.string().required('')),
        }),
      })
    ),
    enumOptions: Yup.array().of(Yup.string().required('')),
  })
  .test({
    test: (values) => {
      if (!values.enumOptions || new Set(values.enumOptions).size === values.enumOptions.length) {
        return true;
      } else {
        return new Yup.ValidationError('All options have to be unique', null, 'enumOptions');
      }
    },
  });

export type FormikValues = InternalApiCustomListSchemaField & {
  childFields: InternalApiCustomListSchemaField[];
};

export const SchemaFieldModal = ({
  schemaFields,
  customListId,
  onChange,
  onError,
  onClose,
  show,
  setShow,
  schemaField,
}: {
  schemaFields?: InternalApiCustomListSchemaField[];
  customListId: string;
  onChange: () => void;
  onError: () => void;
  onClose: () => void;
  show: boolean;
  setShow: (show: boolean) => void;
  schemaField?: InternalApiCustomListSchemaField;
}) => {
  const dispatch = useDispatch();
  const [bannerMessages, setBannerMessages] = useState('');

  const [isCustomListEmpty, setIsCustomListEmpty] = useState(false);
  const screeningUsableFields = useSelector((app: AppState) => app.customList.usableScreeningFields);
  const monitoringUsableFields = useSelector((app: AppState) => app.customList.usableMonitoringFields);
  const { mutate: updateSchemaFields } = useUpdateCustomListEntitySchemaFields(customListId);

  useEffect(() => {
    customListRecordApi
      .getRecords({ customListId, page: 0, size: 1 })
      .then((res) => setIsCustomListEmpty(res.total === 0));
  }, []);

  const initialValues: FormikValues = {
    id: schemaField?.id || uuidv4(),
    displayName: schemaField?.displayName || '',
    fieldName: schemaField?.fieldName || '',
    isMulti: schemaField?.isMulti !== undefined ? schemaField.isMulti : false,
    type: schemaField?.type || InternalApiCustomListSchemaFieldType.STRING,
    visibleInList: schemaField?.visibleInList !== undefined ? schemaField.visibleInList : true,
    visibleInMatches: schemaField?.visibleInMatches !== undefined ? schemaField.visibleInMatches : true,
    childFields:
      (schemaField && schemaFields?.filter((schemaField1) => schemaField1.parentId === schemaField?.id)) || [],
    enumOptions: schemaField?.enumOptions,
    isNullable: schemaField?.isNullable ?? true,
    sortOrder: schemaField?.sortOrder || (schemaFields?.filter((field) => !field.parentId).length || 0) + 1,
  };

  const isEditing = schemaField !== undefined;
  const isFieldEditingDisabled =
    isEditing && (!isCustomListEmpty || screeningUsableFields.length !== 0 || monitoringUsableFields.length !== 0);

  const tooltipText: string = useMemo(() => {
    const usableFieldsDefined = screeningUsableFields?.length !== 0 || monitoringUsableFields?.length !== 0;
    if (usableFieldsDefined) {
      return isCustomListEmpty
        ? "Editing custom list's schema fields is only allowed when no Usable fields have been defined"
        : "Editing custom list's schema fields is only allowed for empty lists and when no Usable fields have been defined";
    } else if (!isCustomListEmpty) {
      return "Editing custom list's schema fields is only allowed for empty lists";
    }
    return '';
  }, [screeningUsableFields, monitoringUsableFields, isCustomListEmpty]);

  const confirm = (values: FormikValues, helpers: FormikHelpers<FormikValues>) => {
    if (isEditing || isCustomListEmpty) {
      submit(values, helpers);
    } else {
      setShow(false);
      dispatch(
        showConfirmationModal({
          title:
            'Are you sure? This field cannot be later removed should there be configuration errors. Fields can only be removed from an empty Custom list.',
          onConfirm() {
            setShow(true);
            submit(values, helpers);
          },
          onCancel() {
            setShow(true);
            helpers.setSubmitting(false);
          },
        })
      );
    }
  };

  const submit = async (values: FormikValues, { setSubmitting, resetForm }: FormikHelpers<FormikValues>) => {
    setBannerMessages('');
    try {
      setSubmitting(true);
      const { childFields, ...attributes } = values;

      updateSchemaFields([
        ...(schemaFields?.filter((field) => field.id !== values.id && field.parentId !== values.id) || []),
        {
          ...attributes,
          type: values.type as InternalApiCustomListSchemaFieldType,
        },
        ...childFields,
      ]);
      resetForm({ values: initialValues });
      onChange();
    } catch (e) {
      onError();

      const res = e as Response;
      if (res.status === 400) {
        const { message } = (await res.json()) as ErrorResponse;
        setBannerMessages(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount
      onSubmit={confirm}
      validationSchema={validationSchema}
    >
      {({ values, isSubmitting, setFieldValue, setFieldTouched, errors, isValid }: FormikProps<FormikValues>) => {
        useEffect(() => {
          if (values.type === InternalApiCustomListSchemaFieldType.OBJECT) {
            setFieldValue('childFields', [...initialValues.childFields]);
          } else {
            setFieldValue('childFields', []);
          }

          if (values.type === InternalApiCustomListSchemaFieldType.ENUM) {
            if (!initialValues.enumOptions) {
              setFieldValue('enumOptions', ['', '']);
            }
          } else {
            setFieldValue('enumOptions', undefined);
          }
        }, [values.type]);

        return (
          <SalvModal
            show={show}
            onHide={() => {
              onClose();
              setBannerMessages('');
            }}
            className={`schema-field-modal ${values.type === 'OBJECT' ? 'object-field' : 'non-object-field'}`}
          >
            <Form>
              <SalvModal.Header>{schemaField ? 'Edit' : 'Add new'} field </SalvModal.Header>
              <SalvModal.Body>
                {bannerMessages &&
                  bannerMessages
                    .split('\n')
                    .map((message, index) => (
                      <SalvBanner
                        key={index}
                        accent={SalvBannerAccent.STRONG}
                        message={message}
                        type={SalvBannerType.DANGER}
                        className={'m-b-8'}
                      />
                    ))}
                <Row>
                  <Col md={6} className='mb-4 pe-3'>
                    <div className='row'>
                      <div className='col'>
                        <SalvTextField
                          label='Display name'
                          description='Descriptive name for a field, for example, First name, Date of birth, etc.'
                          value={values.displayName}
                          onChange={(value: string) => setFieldValue('displayName', value)}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className='mb-4 ps-3'>
                    <div className='row'>
                      <div className='col'>
                        <DisabledField tooltipText={tooltipText} isEditingDisabled={isFieldEditingDisabled}>
                          <SalvTextField
                            label='Field name'
                            description='Name of the attribute, for example, first_name, dob, etc. Field name can only contain letters, numbers or underscores.'
                            value={values.fieldName}
                            disabled={isFieldEditingDisabled}
                            onChange={async (value: string) => {
                              await setFieldValue('fieldName', value);
                              await setFieldTouched('fieldName');
                            }}
                          />
                        </DisabledField>
                        <ErrorMessage
                          name={`fieldName`}
                          render={(message) => (
                            <ReactForm.Control.Feedback type='invalid' className='d-block'>
                              {message}
                            </ReactForm.Control.Feedback>
                          )}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className='mb-4 pe-3'>
                    <DisabledField isEditingDisabled={isFieldEditingDisabled} tooltipText={tooltipText}>
                      <SalvDropdown
                        name='type'
                        label='Input type'
                        options={customListSchemaFieldTypeOptions}
                        value={customListSchemaFieldTypeOptions.find((option) => option.key === values.type)}
                        disabled={isFieldEditingDisabled}
                        onChange={(options) => {
                          if (options[0]) {
                            setFieldValue('type', options[0].value);
                          }
                        }}
                        renderedInBootstrapModal
                      />
                      <SalvCheckbox
                        label={'Multiple values'}
                        description={`Multiple values allows adding several values for the same data field,
                         which are then saved as an array, 
                         for example, adding several bank account numbers.`}
                        selected={values.isMulti}
                        onChange={(isSelected) => setFieldValue('isMulti', isSelected)}
                        disabled={isFieldEditingDisabled}
                      />
                    </DisabledField>
                  </Col>
                  <Col md={6} className='mb-4 ps-3'>
                    <SalvCheckboxGroup
                      label={'Fields visible in'}
                      value={{
                        visibleInMatches: values.visibleInMatches,
                        visibleInList: values.visibleInList,
                      }}
                      onChange={(checkBoxValues) => {
                        setFieldValue('visibleInMatches', checkBoxValues.includes('visibleInMatches'));
                        setFieldValue('visibleInList', checkBoxValues.includes('visibleInList'));
                      }}
                    >
                      <SalvGroupedCheckbox label={'Matches'} value={'visibleInMatches'} />
                      <SalvGroupedCheckbox label={'List'} value={'visibleInList'} />
                    </SalvCheckboxGroup>
                    <DisabledField tooltipText={tooltipText} isEditingDisabled={isFieldEditingDisabled}>
                      <SalvRadioGroup
                        label={'Field is optional/required'}
                        value={values.isNullable}
                        onChange={(value) => {
                          setFieldValue('isNullable', value === 'true');
                        }}
                        disabled={isFieldEditingDisabled}
                        className={'m-t-32'}
                      >
                        <SalvRadio value={true} label={'Optional'} />
                        <SalvRadio value={false} label={'Required'} />
                      </SalvRadioGroup>
                    </DisabledField>
                  </Col>
                </Row>
                {values.type === InternalApiCustomListSchemaFieldType.OBJECT && (
                  <SchemaChildFieldsTable values={values} setFieldValue={setFieldValue} errors={errors} />
                )}
                {values.type === InternalApiCustomListSchemaFieldType.ENUM && values.enumOptions && (
                  <SchemaDropdownOptions
                    dropdownOptions={values.enumOptions}
                    setDropdownOptions={async (values) => {
                      await setFieldValue('enumOptions', values);
                      await setFieldTouched('enumOptions');
                    }}
                  />
                )}
              </SalvModal.Body>
              <SalvModal.Footer>
                <Button
                  variant='secondary'
                  onClick={() => {
                    onClose();
                    setBannerMessages('');
                  }}
                >
                  Close
                </Button>
                <Button disabled={isSubmitting || !isValid} type='submit' variant='primary'>
                  Save
                </Button>
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default SchemaFieldModal;
